<script setup>
/**
 * THE ONE OVERLAY THIS TREE OWNS.
 *
 * ── WHY IT EXISTS ──────────────────────────────────────────────────────────────────
 *
 * `Dialog`, `Popover`, `SidePanel`, `DropdownContainer`, `CaretAnchoredPicker` and
 * `EmptyStateLayout` all reach `TeleportWithDirection`, which reads the writing direction
 * from `useMapGetter('accounts/isRTL')`. That getter is a lazy computed over
 * `vm.proxy.$store`, so in a unit mount with no store it does not fail in `setup` where a
 * test could see it — it fails the first time the RENDER reads it, inside a render effect,
 * on a scheduler job that runs after the triggering test has already resolved. Vue turns
 * that into an unhandled rejection. An unhandled rejection cannot fail an assertion; it can
 * only fail the process exit code, which is why a run once printed `1436 passed` and exited
 * 1 — and killed the `waslo-infra` image build on BOTH environments on 2026-09-25.
 * `Dialog` adds a second, unrelated problem: `showModal()`, which jsdom does not implement.
 *
 * So the direction is read off the DOM instead, from the attribute Chatwoot already puts on
 * its root element. The same pattern is in-tree twice — `composables/useDropdownPosition.js`
 * and `components-next/DraggableReorderList/DraggableReorderList.vue` — and it needs no
 * store, no router, no i18n and no plugin of any kind. `specs/waslo-drawer.spec.js` mounts
 * this component with nothing installed and proves it.
 *
 * ── WHAT IT IS NOT ─────────────────────────────────────────────────────────────────
 *
 * Not a re-skin. The visual treatment — `bg-n-alpha-3`, the blur, `rounded-xl`, the footer
 * button pair — is copied from `components-next/dialog/Dialog.vue` on purpose, so a screen
 * converted from one to the other looks unchanged. The props are Dialog's props with the
 * same names and the same defaults, so a call site moves by changing the import and nothing
 * else, with two deliberate exceptions:
 *
 *   • `position` ('center' | 'top') and a drawer's edge placement are ONE prop, `placement`
 *     ('center' | 'top' | 'end'). Two props that both decide where the panel sits is how you
 *     get a combination nobody has looked at.
 *   • THERE ARE NO DEFAULT BUTTON LABELS. `Dialog` falls back to `t('DIALOG.BUTTONS.CANCEL')`,
 *     which needs vue-i18n installed — the exact kind of ambient dependency this component
 *     exists to shed. A button with no label is not rendered at all, because a button reading
 *     `DIALOG.BUTTONS.CANCEL`, or reading English inside an Arabic workspace, is worse than a
 *     button that is visibly missing. All nine current `Dialog` call sites in this tree
 *     already pass both labels, so nothing loses a button by moving.
 *
 * ── THE BEHAVIOUR IT OWES A MODAL ──────────────────────────────────────────────────
 *
 * `<dialog>.showModal()` gave four things for free, and each one is written out here because
 * it is not free any more: the backdrop, focus moving into the panel, focus never leaving it
 * while it is open, and Escape. The fifth — focus RETURNING to whatever opened the drawer —
 * the native element also gives, and losing it is the one that is invisible in a screenshot
 * and obvious to anyone using a keyboard.
 */
import { computed, nextTick, onBeforeUnmount, ref } from 'vue';

import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  /** 'alert' paints the confirm button ruby, exactly as Dialog's does. */
  type: {
    type: String,
    default: 'edit',
    validator: value => ['alert', 'edit'].includes(value),
  },
  title: { type: String, default: '' },
  description: { type: String, default: '' },
  /** No fallback. An empty label hides its button — see the header. */
  cancelButtonLabel: { type: String, default: '' },
  confirmButtonLabel: { type: String, default: '' },
  disableConfirmButton: { type: Boolean, default: false },
  isLoading: { type: Boolean, default: false },
  showCancelButton: { type: Boolean, default: true },
  showConfirmButton: { type: Boolean, default: true },
  overflowYAuto: { type: Boolean, default: false },
  width: {
    type: String,
    default: 'lg',
    validator: value => ['3xl', '2xl', 'xl', 'lg', 'md', 'sm'].includes(value),
  },
  /**
   * 'center' and 'top' are Dialog's two `position` values. 'end' is the drawer proper: a
   * full-height panel against the inline-end edge, which is the right edge in English and
   * the left edge in Arabic, decided by the `dir` below rather than by a class.
   */
  placement: {
    type: String,
    default: 'center',
    validator: value => ['center', 'top', 'end'].includes(value),
  },
  /** Where the overlay is teleported. A test may point it somewhere it can read. */
  to: { type: String, default: 'body' },
});

const emit = defineEmits(['confirm', 'close']);

const isOpen = ref(false);
const direction = ref('ltr');
const panelRef = ref(null);

/** Whatever had focus when the drawer opened, so it can have it back. */
let returnFocusTo = null;
/** The page's own `overflow`, restored verbatim — including the empty string. */
let bodyOverflow = null;

/**
 * THE WHOLE POINT OF THE COMPONENT, in one expression: the direction comes from the DOM
 * Chatwoot already renders, not from a Vuex getter. Re-read on every open, because the
 * workspace language can change between two openings of the same drawer.
 */
const readDirection = () =>
  document.querySelector('#app[dir]')?.getAttribute('dir') === 'rtl'
    ? 'rtl'
    : 'ltr';

const maxWidthClass = computed(
  () =>
    ({
      '3xl': 'max-w-3xl',
      '2xl': 'max-w-2xl',
      xl: 'max-w-xl',
      lg: 'max-w-lg',
      md: 'max-w-md',
      sm: 'max-w-sm',
    })[props.width] ?? 'max-w-md'
);

/**
 * Logical only. `justify-end` and `ms-auto` follow the flex direction the `dir` attribute
 * sets, so the end drawer needs no `ltr:`/`rtl:` pair — and half a pair fails the lint.
 */
const layoutClass = computed(() => {
  if (props.placement === 'end') return 'justify-end items-stretch';
  if (props.placement === 'top') return 'justify-center items-start pt-8';
  return 'justify-center items-center';
});

const panelClass = computed(() =>
  props.placement === 'end'
    ? 'h-full w-full rounded-s-xl'
    : `w-full ${maxWidthClass.value} rounded-xl`
);

const FOCUSABLE = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  '[contenteditable]',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const focusableItems = () => {
  const panel = panelRef.value;
  if (!panel) return [];
  return Array.from(panel.querySelectorAll(FOCUSABLE)).filter(
    element => element.getAttribute('aria-hidden') !== 'true'
  );
};

/** Detaches the Escape listener this opening attached. Held as a closure rather than as a
 *  named handler so that `close` can be defined before `open` and nothing is hoisted. */
let detachEscape = null;

/** Undoes everything `open` did to the PAGE, as opposed to what it did to the drawer. Called
 *  both by `close` and by `onBeforeUnmount`, because a drawer can be unmounted while open. */
const releasePage = () => {
  detachEscape?.();
  detachEscape = null;
  if (bodyOverflow !== null) {
    document.body.style.overflow = bodyOverflow;
    bodyOverflow = null;
  }
};

const close = () => {
  if (!isOpen.value) return;
  isOpen.value = false;
  releasePage();
  emit('close');
  const target = returnFocusTo;
  returnFocusTo = null;
  // `isConnected`, because the thing that opened the drawer may have been removed by what
  // the drawer did. Focusing a detached node silently moves focus to <body>.
  if (target && target.isConnected) target.focus();
};

const open = async () => {
  if (isOpen.value) return;
  returnFocusTo =
    document.activeElement && document.activeElement !== document.body
      ? document.activeElement
      : null;
  direction.value = readDirection();
  isOpen.value = true;
  bodyOverflow = document.body.style.overflow;
  document.body.style.overflow = 'hidden';

  const onEscape = event => {
    if (event.key !== 'Escape' || !isOpen.value) return;
    event.stopPropagation();
    close();
  };
  document.addEventListener('keydown', onEscape);
  detachEscape = () => document.removeEventListener('keydown', onEscape);

  await nextTick();
  // The panel itself is focusable (`tabindex="-1"`), so there is always somewhere for focus
  // to land — a drawer holding only text would otherwise leave focus behind on the page,
  // where the next Tab walks the page behind the backdrop.
  const [first] = focusableItems();
  (first ?? panelRef.value)?.focus();
};

const confirm = () => emit('confirm');

/**
 * Tab and Shift+Tab, kept inside. Without this the page behind the backdrop is still
 * reachable by keyboard while being unreachable by mouse, which is the worst of both.
 */
const onTrapKeydown = event => {
  if (event.key !== 'Tab') return;
  const items = focusableItems();
  if (!items.length) {
    event.preventDefault();
    panelRef.value?.focus();
    return;
  }
  const first = items[0];
  const last = items[items.length - 1];
  const active = document.activeElement;
  const inside = panelRef.value?.contains(active);
  if (event.shiftKey && (!inside || active === first)) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && (!inside || active === last)) {
    event.preventDefault();
    first.focus();
  }
};

onBeforeUnmount(() => {
  // A drawer unmounted while open (a route change, a parent `v-if`) must not leave the page
  // unscrollable or the document listener attached.
  releasePage();
});

defineExpose({ open, close });
</script>

<template>
  <Teleport :to="to">
    <div v-if="isOpen" :dir="direction" data-waslo-drawer-root>
      <!--
        One element is both the backdrop and the positioner. `@click.self` is what makes a
        click on the panel — or on anything inside it — not a click on the backdrop, without
        a `@click.stop` on the panel that would also swallow clicks the page may want.
      -->
      <div
        class="fixed inset-0 z-50 flex p-4 bg-n-alpha-black1 backdrop-blur-[4px]"
        :class="layoutClass"
        data-waslo-drawer-backdrop
        @click.self="close"
      >
        <div
          ref="panelRef"
          role="dialog"
          aria-modal="true"
          tabindex="-1"
          :aria-label="title || undefined"
          class="flex flex-col gap-6 p-6 text-start align-middle shadow-xl outline-none bg-n-alpha-3 backdrop-blur-[100px]"
          :class="[
            panelClass,
            overflowYAuto ? 'overflow-y-auto' : 'overflow-visible',
          ]"
          data-waslo-drawer-panel
          @keydown="onTrapKeydown"
        >
          <div v-if="title || description" class="flex flex-col gap-2">
            <h3
              v-if="title"
              class="text-base font-medium leading-6 text-n-slate-12"
            >
              {{ title }}
            </h3>
            <slot name="description">
              <p v-if="description" class="mb-0 text-sm text-n-slate-11">
                {{ description }}
              </p>
            </slot>
          </div>

          <!--
            A form, so Enter confirms from any field inside — the one affordance `<dialog>`
            did not provide and `Dialog` added for the same reason.
          -->
          <form class="flex flex-col gap-6" @submit.prevent="confirm">
            <slot />

            <slot name="footer">
              <div
                v-if="
                  (showCancelButton && cancelButtonLabel) ||
                  (showConfirmButton && confirmButtonLabel)
                "
                class="flex items-center justify-between w-full gap-3"
              >
                <Button
                  v-if="showCancelButton && cancelButtonLabel"
                  variant="faded"
                  color="slate"
                  type="button"
                  class="w-full"
                  :label="cancelButtonLabel"
                  data-waslo-drawer-cancel
                  @click="close"
                />
                <Button
                  v-if="showConfirmButton && confirmButtonLabel"
                  :color="type === 'edit' ? 'blue' : 'ruby'"
                  type="submit"
                  class="w-full"
                  :label="confirmButtonLabel"
                  :is-loading="isLoading"
                  :disabled="disableConfirmButton || isLoading"
                  data-waslo-drawer-confirm
                />
              </div>
            </slot>
          </form>
        </div>
      </div>
    </div>
  </Teleport>
</template>
